import { HTMLAttributes, useState } from "react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { DictType } from "@pma/types"
import { Button } from "../common/button"
import { Trash2Icon } from "lucide-react"
import useTranslator from "@pma/hooks/use-translator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { PROJECT_RESTORE, SERVER_URL } from "@pma/utils/api-routes"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { toast } from "@pma/hooks/use-toast"
import { useAuth } from "@pma/stores/auth-store"

interface Props extends HTMLAttributes<HTMLDivElement> {
  project: DictType
}

const RestoreProject = ({ project, children, className }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const [searchParam] = useSearchParams()
  const [open, setOpen] = useState(false)
  const newParams = new URLSearchParams(searchParam)

  const restoreProjectApi = async () => {
    const response = await axios.post(
      SERVER_URL + PROJECT_RESTORE,
      {
        projects_id: [project?.id],
        organization_id: authConfig?.last_organization_id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const restoreProjectMutation = useMutation({
    mutationKey: ["restore-project", project?.id],
    mutationFn: restoreProjectApi,
    onSuccess: (data) => {
      if (children) {
        newParams.set("revalidate", "projects")
        navigate(PROJECTS_PAGE_ROUTE + `?${newParams.toString()}`)
      } else {
        newParams.set("revalidate", "projects")
        navigate(`?${newParams.toString()}`)
      }
      setOpen(false)

      toast({
        title: translate("Success"),
        description: `${project?.title} ${translate("has been restored")}`,
        variant: "success",
      })
    },
    onError: (error) => {
      setOpen(false)
      toast({
        title: translate("Error"),
        description: `${translate("Unable to restore")} ${project?.title} ${translate("project")}`,
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger className={className} onClick={()=> setOpen(true)}>
        <Tooltip>
          <TooltipTrigger>
            {children ? (
              children
            ) : (
              <Button variant={"ghost"} className='text-xs' size={"icon"}>
                <Trash2Icon size={14} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1'>
            <h4>
              {translate(`Restore Project`)} {project?.title}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {project?.title}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.{" "}
            {translate("This will restore the project")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>{translate("Cancel")}</AlertDialogCancel>
          <Button
            disabled={restoreProjectMutation?.isPending}
            loading={restoreProjectMutation?.isPending}
            onClick={() => restoreProjectMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreProject
